// Нативный запрос оценки приложения в сторах: SKStoreReviewController на iOS,
// In-App Review API на Android — через @capacitor-community/in-app-review.
//
// Диалог системный (без наших i18n-ключей), и ОС сама решает, показать ли его
// (Apple: не больше 3 раз в год на устройство), поэтому наша задача — просить
// только в «победный» момент и только у того, кто может оценить продукт:
//   1. только нативное приложение (веб и PWA — никогда);
//   2. только владелец компании (роль superadmin);
//   3. не раньше 30 дней с создания главной компании (main_company.created_at):
//      раньше пользователь продукт не освоил (решение владельца продукта);
//   4. триггер — успешное действие (создана бронь, принята машина), с задержкой
//      1.5 с после закрытия диалога, чтобы не перекрывать тост;
//   5. не чаще раза в 120 дней на устройстве — своя метка независимо от лимита
//      Apple, в нативном хранилище (@capacitor/preferences: WebView-localStorage
//      ОС может очистить, и интервал обнулился бы); никогда на симуляторе и
//      эмуляторе — по Device.getInfo().isVirtual (разбор userAgent не работает:
//      UA iOS-симулятора слова Simulator не содержит, ревью PR #433);
//   6. не раньше пятого «победного» момента на этом устройстве (счётчик в том же
//      хранилище): календарный срок сам по себе слабый сигнал — компания могла
//      начать работать вчера, а зарегистрироваться два месяца назад;
//   7. не в триале (plan.trial_end в будущем — тот же признак, что у остального
//      фронта; имя тарифа «Старт» признаком НЕ является: на нём стартует каждая
//      регистрация и остаются платящие клиенты младшего тарифа) и не под paywall
//      (даннинг): человек, который решает, не отменить ли подписку, оценку не улучшит;
//   8. метка «показали» и сброс счётчика — только после 1.5-секундной паузы (если
//      таймер «проспал» в фоне сильно дольше — пропуск) и только если приложение
//      активно (App.getState) до и после записи метки: iOS-плагин молча резолвится
//      без диалога, когда сцена не на переднем плане, и метка на 120 дней сгорела
//      бы впустую; не записалась метка — не показываем;
//   9. вызовы сериализованы: два успеха подряд (Preferences без атомарного
//      инкремента) не должны прочитать одно и то же состояние и показать диалог дважды.
//
// Решение вынесено в чистую функцию shouldRequestReview — её и покрывают юниты;
// requestReviewAfterSuccess только собирает входы из стора/платформы и зовёт
// плагин. Ошибка никогда не должна ломать основной сценарий: результат — булево
// «попросили или нет», а сбои уходят в Rollbar (ошибка плагина — warning, это
// ожидаемый отказ платформы; ошибка нашей логики — error).

import Vue from "vue";
import moment from "moment";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { Preferences } from "@capacitor/preferences";
import { isVirtualDevice } from "@/utils/nativeDevice";
import { InAppReview } from "@capacitor-community/in-app-review";
import { OWNER_ROLE } from "@/constants/roles";

export const DEFAULT_MIN_DAYS = 30;
export const DEFAULT_MIN_SUCCESS_EVENTS = 5;
export const REPEAT_DAYS = 120;
export const REVIEW_DELAY_MS = 1500;
// Если пауза растянулась сильно дольше (таймеры WebView в фоне замораживаются),
// показ уже не связан с «победой» — пропускаем.
export const REVIEW_DELAY_TOLERANCE_MS = 10 * 1000;
// Ответ плагина ждём ограниченно: на Android колбэк Play Core может не прийти,
// а очередь вызовов иначе встанет до перезапуска приложения.
export const REVIEW_PLUGIN_TIMEOUT_MS = 15 * 1000;
export const STORAGE_KEY = "app_review_last_prompt_at";
export const SUCCESS_COUNT_KEY = "app_review_success_count";
export const REVIEW_ROLE = OWNER_ROLE;

const DAY_MS = 24 * 60 * 60 * 1000;

// Dev-переопределение порога: в production-сборке переменная игнорируется,
// чтобы случайный .env не выпустил порог в релиз.
function resolveDevThreshold(env, key, fallback) {
  if (!env || env.NODE_ENV === "production") return fallback;
  const raw = env[key];
  if (raw === undefined || raw === null || raw === "") return fallback;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 0) return fallback;
  return parsed;
}

// Порог возраста компании (VUE_APP_REVIEW_MIN_DAYS, например 0 — просить сразу).
export function resolveMinDays(env = process.env) {
  return resolveDevThreshold(env, "VUE_APP_REVIEW_MIN_DAYS", DEFAULT_MIN_DAYS);
}

// Порог «победных» моментов на устройстве (VUE_APP_REVIEW_MIN_EVENTS).
export function resolveMinSuccessEvents(env = process.env) {
  return resolveDevThreshold(env, "VUE_APP_REVIEW_MIN_EVENTS", DEFAULT_MIN_SUCCESS_EVENTS);
}

// Триал — plan.trial_end в будущем, как в Drawer/TimelineCalendar/ShiftTimeline:
// через moment, потому что trial_end — Rails t.date («YYYY-MM-DD»), и new Date()
// прочёл бы его как UTC-полночь, а не локальную (к западу от UTC триал кончался
// бы на оффсет раньше остального фронта). Нет плана или даты — не триал
// (ревью PR #433: имя тарифа «Старт» — не признак).
export function isOnTrial(mainCompany, now = new Date()) {
  const plan = mainCompany && mainCompany.plan;
  const trialEnd = plan && plan.trial_end;
  if (!trialEnd) return false;
  const end = moment(trialEnd);
  const current = moment(now);
  if (!end.isValid() || !current.isValid()) return false;
  return end.isAfter(current);
}

// Строка/Date → миллисекунды; всё, что не парсится, → null.
function toTime(value) {
  if (value === null || value === undefined || value === "") return null;
  const time = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
}

export function shouldRequestReview({
  isNative,
  mainCompanyCreatedAt,
  lastPromptAt,
  role,
  successCount = 0,
  trial = false,
  paywallTier = "none",
  now = new Date(),
  minDays = resolveMinDays(),
  minSuccessEvents = resolveMinSuccessEvents(),
  repeatDays = REPEAT_DAYS,
}) {
  if (isNative !== true) return false;
  if (role !== REVIEW_ROLE) return false;
  if (trial === true) return false;
  // Любой paywall (soft/hard) — компания в даннинге, не время просить оценку.
  if (paywallTier !== "none") return false;
  if (!(Number(successCount) >= minSuccessEvents)) return false;

  const nowTime = toTime(now);
  const createdTime = toTime(mainCompanyCreatedAt);
  // Нет даты (старый payload API, разлогин) — не показываем: лучше молчать,
  // чем спросить оценку у того, кто зарегистрировался вчера.
  if (nowTime === null || createdTime === null) return false;
  // Пороги неотрицательны, поэтому сравнение с ними отсекает и отрицательный
  // возраст (компания «из будущего» при сбитых часах) — отдельный guard на
  // `< 0` был бы мёртвым кодом (ревью PR #433, мутация выжила).
  const companyAge = nowTime - createdTime;
  if (companyAge < minDays * DAY_MS) return false;

  const lastTime = toTime(lastPromptAt);
  if (lastTime !== null) {
    // Метка из будущего — тоже «недавно», а не «давно»: отрицательная разница
    // меньше любого неотрицательного порога.
    const sinceLast = nowTime - lastTime;
    if (sinceLast < repeatDays * DAY_MS) return false;
  }
  return true;
}

// Метка последнего показа — в нативном key/value (Preferences), не в
// localStorage WebView. Интерфейс сведён к get/set по ключу, чтобы в юнитах
// подменять его объектом без плагина.
export const preferencesStorage = {
  async get(key) {
    const { value } = await Preferences.get({ key });
    return value;
  },
  async set(key, value) {
    await Preferences.set({ key, value });
  },
};

export async function readLastPromptAt(storage) {
  try {
    const value = await storage.get(STORAGE_KEY);
    return value === undefined ? null : value;
  } catch (e) {
    return null;
  }
}

// true — метка записана. Без метки показывать нельзя: следующие пять успехов
// показали бы диалог снова задолго до 120 дней.
async function writeLastPromptAt(storage, value, report) {
  try {
    await storage.set(STORAGE_KEY, value);
    return true;
  } catch (error) {
    report("appReviewService: cannot persist last prompt mark", error, "warning");
    return false;
  }
}

// Счётчик «победных» моментов на устройстве: +1 на каждый успешный триггер,
// возвращает новое значение. Нечитаемое хранилище = 0 → станет 1; незаписанное
// значение — warning, счётчик тогда просто не растёт (показ не случится —
// fail-closed).
export async function incrementSuccessCount(storage, report) {
  let current = 0;
  try {
    const parsed = Number(await storage.get(SUCCESS_COUNT_KEY));
    if (Number.isFinite(parsed) && parsed > 0) current = Math.floor(parsed);
  } catch (e) {
    current = 0;
  }
  const next = current + 1;
  try {
    await storage.set(SUCCESS_COUNT_KEY, String(next));
  } catch (error) {
    report("appReviewService: cannot persist success counter", error, "warning");
  }
  return next;
}

// После показа счётчик начинается заново: следующий показ — не раньше пяти
// новых побед (и 120 дней).
async function resetSuccessCount(storage, report) {
  try {
    await storage.set(SUCCESS_COUNT_KEY, "0");
  } catch (error) {
    report("appReviewService: cannot reset success counter", error, "warning");
  }
}

// Приложение на переднем плане? Если платформа не ответила — считаем, что нет:
// пропустить показ безопаснее, чем сжечь метку.
async function defaultIsAppActive() {
  const state = await CapacitorApp.getState();
  return state.isActive === true;
}

// Симулятор/эмулятор — только по факту от платформы (utils/nativeDevice.js).
// Если платформа не ответила, считаем устройство виртуальным: пропустить
// необязательный диалог безопаснее, чем сжечь 120-дневную метку там, где
// диалог не показывается.
const defaultIsVirtual = isVirtualDevice;

function defaultReportError(text, error, level = "error") {
  const rollbar = Vue.prototype.$rollbar;
  if (rollbar && typeof rollbar[level] === "function") {
    rollbar[level](text, error);
  } else {
    console.warn(text, error);
  }
}

function delay(schedule, ms) {
  return new Promise((resolve) => schedule(resolve, ms));
}

// Вызывать ПОСЛЕ успешного ответа API и закрытия диалога. Возвращает true, если
// плагин был вызван без ошибки (не «диалог показан» — ОС этого не сообщает);
// никогда не бросает.
//
// deps — точка подмены для юнитов; в проде используются дефолты.
//
// Вызовы выстроены в очередь: следующий начинается после завершения предыдущего
// (включая паузу и вызов плагина), поэтому два успеха подряд видят уже
// обновлённые счётчик и метку.
let queue = Promise.resolve();

export function requestReviewAfterSuccess(store, deps = {}) {
  // Очередь не должна отравиться, а вызывающий — получить отвергнутый промис:
  // runReviewAfterSuccess не бросает, но если бросит сам reportError, и очередь,
  // и возвращаемое значение сводятся к false.
  queue = queue.then(() => runReviewAfterSuccess(store, deps)).catch(() => false);
  return queue;
}

function withTimeout(promise, timeout) {
  return Promise.race([promise, timeout.then(() => Symbol.for("appReview.timeout"))]);
}

async function runReviewAfterSuccess(store, deps = {}) {
  const report = deps.reportError || defaultReportError;
  const requestReview = deps.requestReview || (() => InAppReview.requestReview());
  let storage;
  let now;

  try {
    const getters = (store && store.getters) || {};
    const mainCompany = getters.getMainCompany || {};
    const currentUser = getters.getCurrentUser || {};
    const paywall = getters.getPaywall || {};
    storage = deps.storage || preferencesStorage;
    now = deps.now ? deps.now() : new Date();
    const isNative = deps.isNative !== undefined ? deps.isNative : Capacitor.isNativePlatform();
    const minDays = deps.minDays !== undefined ? deps.minDays : resolveMinDays();
    const minSuccessEvents =
      deps.minSuccessEvents !== undefined ? deps.minSuccessEvents : resolveMinSuccessEvents();

    // Веб/PWA — без единого обращения к плагинам.
    if (isNative !== true) return false;

    // Счётчик растёт на каждом успешном действии на устройстве, независимо от
    // роли и возраста компании: сигнал — «устройством реально работают».
    const successCount = await incrementSuccessCount(storage, report);

    const base = {
      isNative,
      mainCompanyCreatedAt: mainCompany.created_at,
      role: currentUser.role,
      successCount,
      trial: isOnTrial(mainCompany, now),
      paywallTier: paywall.tier || "none",
      now,
      minDays,
      minSuccessEvents,
    };

    // Дешёвые проверки (роль, триал, paywall, возраст, счётчик) — до чтения метки и Device.
    if (!shouldRequestReview({ ...base, lastPromptAt: null })) return false;
    if (!shouldRequestReview({ ...base, lastPromptAt: await readLastPromptAt(storage) })) return false;

    let virtual = true;
    try {
      virtual = await (deps.isVirtual || defaultIsVirtual)();
    } catch (error) {
      report("appReviewService: Device.getInfo failed, skipping review prompt", error, "warning");
    }
    if (virtual) return false;

    const schedule = deps.schedule || ((fn, ms) => setTimeout(fn, ms));
    const clock = deps.clock || (() => Date.now());
    const startedAt = clock();
    await delay(schedule, REVIEW_DELAY_MS);
    // Таймер в фоне мог «проспать» часы: показ тогда оторван от «победы».
    if (clock() - startedAt > REVIEW_DELAY_MS + REVIEW_DELAY_TOLERANCE_MS) return false;

    // За паузу пользователь мог свернуть приложение: iOS-плагин тогда молча
    // резолвится без диалога. Метку и сброс счётчика делаем только на переднем
    // плане; иначе следующий успех попробует снова. Сбой App.getState — как «в фоне».
    const isAppActive = deps.isAppActive || defaultIsAppActive;
    const checkActive = async () => {
      try {
        return await isAppActive();
      } catch (error) {
        report("appReviewService: App.getState failed, skipping review prompt", error, "warning");
        return false;
      }
    };
    if (!(await checkActive())) return false;

    // Метку ставим до вызова плагина: ОС не сообщает, показала ли диалог, а
    // повторять попытку на каждом действии при молчащем плагине не нужно. Не
    // записалась — не показываем.
    const previousMark = await readLastPromptAt(storage);
    if (!(await writeLastPromptAt(storage, now.toISOString(), report))) return false;

    // Пока писали метку, приложение могли свернуть: откатываем метку, счётчик
    // ещё не тронут — следующий успех попробует снова.
    if (!(await checkActive())) {
      await writeLastPromptAt(storage, previousMark === null ? "" : previousMark, report);
      return false;
    }
    await resetSuccessCount(storage, report);
  } catch (error) {
    // Ошибка нашей логики — не должна трогать сценарий, но должна быть видна.
    report("appReviewService: decision failed", error, "error");
    return false;
  }

  try {
    const timeout = deps.timeout
      ? deps.timeout(REVIEW_PLUGIN_TIMEOUT_MS)
      : new Promise((resolve) => setTimeout(resolve, REVIEW_PLUGIN_TIMEOUT_MS));
    const outcome = await withTimeout(Promise.resolve().then(requestReview), timeout);
    if (outcome === Symbol.for("appReview.timeout")) {
      report("appReviewService: requestReview timed out", new Error("timeout"), "warning");
      return false;
    }
    return true;
  } catch (error) {
    // Отказ платформы (UNIMPLEMENTED, нет Play Services) — ожидаемый, warning.
    report("appReviewService: requestReview failed", error, "warning");
    return false;
  }
}

export default { shouldRequestReview, requestReviewAfterSuccess };
